import React, { useRef, useState, useCallback } from 'react';
import { Box, Button, Typography, IconButton } from '@mui/material';
import {
  CloudUpload as CloudUploadIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { formatFileSize } from '../../utils/formatters';

/**
 * FileDropZone — the app's single file-picker primitive.
 *
 * Adds native HTML5 drag-and-drop as an ADDITION to click-to-browse: the user can
 * either click to open the OS file explorer or drag a file onto the zone. It only
 * STAGES / hands the chosen File to the caller via onFile — it never uploads. The
 * caller keeps its own validation (accept/size), upload endpoint, and gating; drop
 * mirrors choose exactly (so a surface that auto-uploads on choose passes an onFile
 * that uploads, and a stage-then-Upload surface passes a set-file callback).
 *
 * Two modes. SINGLE (the default) is unchanged: one `file` in, `onFile` out. MULTIPLE (opt-in via
 * `multiple`, added T3-129 for payment-match proofs) stages a LIST — `files` in, `onFiles` out,
 * `onRemoveAt` to drop one. Everything multi-specific is gated on the flag, so the single-file
 * callers are untouched.
 *
 * ⚠ `onFiles` receives only the NEWLY PICKED BATCH, not the accumulated list. A file input only
 * ever reports its own last selection, so the PARENT owns accumulation (and is therefore also the
 * right place to dedupe). A parent that wants "pick two, then two more = four" does:
 *     onFiles={(batch) => setFiles(prev => [...prev, ...batch])}
 *
 * Props:
 *   file        — SINGLE: the staged File (or null); when set, shows name + size + clear (×)
 *   onFile      — SINGLE: (File|null) => void; called on choose, drop, or clear
 *   multiple    — opt into MULTIPLE mode (default false; keeps every existing caller identical)
 *   files       — MULTIPLE: array of staged Files; rendered as a removable list
 *   onFiles     — MULTIPLE: (File[]) => void; the newly chosen/dropped batch only (see above)
 *   onRemoveAt  — MULTIPLE: (index) => void; remove one staged file. Index-based so duplicate
 *                 filenames stay unambiguous.
 *   accept      — input `accept` string (per surface; filters the file picker)
 *   disabled    — block interaction (e.g. upload in flight, or slot already done)
 *   label       — small caption shown above the zone
 *   helperText  — hint under the prompt (e.g. "PDF, DOC or DOCX. Max 10 MB.")
 *   error       — optional error string (renders red under the zone)
 *   variant     — 'box' (default dashed drop area) | 'button' (compact dashed button)
 *   dense       — tighter padding for inline rows
 *   promptText  — override the idle prompt text
 */
const FileDropZone = ({
  file = null,
  onFile,
  multiple = false,
  files = null,
  onFiles,
  onRemoveAt,
  accept,
  disabled = false,
  label,
  helperText,
  error,
  variant = 'box',
  dense = false,
  promptText,
}) => {
  const inputRef = useRef(null);
  // Enter/leave counter so dragging over nested children doesn't flicker the state.
  const dragDepth = useRef(0);
  const [isDragging, setIsDragging] = useState(false);

  const openPicker = useCallback(() => {
    if (!disabled) inputRef.current?.click();
  }, [disabled]);

  const handleInputChange = (e) => {
    const picked = Array.from(e.target.files || []);
    // Reset so choosing the same file again re-fires onChange.
    e.target.value = '';
    if (multiple) {
      if (picked.length) onFiles?.(picked);
      return;
    }
    const f = picked[0] || null;
    if (f) onFile?.(f);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (inputRef.current) inputRef.current.value = '';
    onFile?.(null);
  };

  // MULTIPLE: remove one staged file. stopPropagation matters in the 'box' variant, whose outer
  // Box is itself clickable — without it, removing a file would also reopen the picker.
  const handleRemoveAt = (index) => (e) => {
    e.stopPropagation();
    if (inputRef.current) inputRef.current.value = '';
    onRemoveAt?.(index);
  };

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };

  const handleDragEnter = (e) => {
    stop(e);
    if (disabled) return;
    dragDepth.current += 1;
    setIsDragging(true);
  };
  const handleDragOver = (e) => {
    stop(e); // required for onDrop to fire
    if (!disabled && !isDragging) setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    stop(e);
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  };
  const handleDrop = (e) => {
    stop(e);
    dragDepth.current = 0;
    setIsDragging(false);
    if (disabled) return;
    // Drop mirrors choose exactly, in both modes.
    const dropped = Array.from(e.dataTransfer?.files || []);
    if (multiple) {
      if (dropped.length) onFiles?.(dropped);
      return;
    }
    const f = dropped[0]; // first file only
    if (f) onFile?.(f);
  };

  const dropHandlers = {
    onDragEnter: handleDragEnter,
    onDragOver: handleDragOver,
    onDragLeave: handleDragLeave,
    onDrop: handleDrop,
  };

  const hiddenInput = (
    <input
      ref={inputRef}
      type="file"
      accept={accept}
      // `undefined` rather than `false` so the rendered DOM stays byte-identical for the
      // single-file callers (React omits the attribute entirely for undefined).
      multiple={multiple || undefined}
      onChange={handleInputChange}
      style={{ display: 'none' }}
    />
  );

  const stagedList = multiple ? (files || []) : [];
  const hasStaged = multiple ? stagedList.length > 0 : !!file;

  // Border colour: dragging (primary) > staged (success) > idle (divider).
  const borderColor = isDragging ? 'primary.main' : (hasStaged ? 'success.main' : 'divider');

  /** One staged file row: name (size) ×. Shared by both variants and both modes. */
  const StagedRow = ({ f, onRemove, align = 'left' }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0, justifyContent: align === 'center' ? 'center' : 'flex-start' }}>
      <AttachFileIcon fontSize="small" color="action" sx={{ flexShrink: 0 }} />
      <Box sx={{ flex: 1, textAlign: align, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {f?.name}{' '}
        {f?.size != null && (
          <Typography component="span" variant="caption" color="text.secondary">({formatFileSize(f.size)})</Typography>
        )}
      </Box>
      {!disabled && onRemove && (
        <IconButton size="small" onClick={onRemove} component="span" aria-label={`Remove ${f?.name || 'file'}`} sx={{ flexShrink: 0 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  /** MULTIPLE: the removable list of staged files, rendered above the (still clickable) prompt. */
  const stagedMultiList = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, width: '100%' }}>
      {stagedList.map((f, i) => (
        <StagedRow key={`${f.name}|${f.size}|${f.lastModified}|${i}`} f={f} onRemove={handleRemoveAt(i)} />
      ))}
    </Box>
  );

  const stagedInline = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, overflow: 'hidden' }}>
      <Box sx={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {file?.name}{' '}
        {file?.size != null && (
          <Typography component="span" variant="caption" color="text.secondary">({formatFileSize(file.size)})</Typography>
        )}
      </Box>
      {!disabled && (
        <IconButton size="small" onClick={handleClear} component="span" aria-label="Remove file">
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </Box>
  );

  // ---- 'button' variant: compact outlined dashed button (ProofUploadField look) ----
  if (variant === 'button') {
    return (
      <Box>
        {label && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {label}
          </Typography>
        )}
        {/* MULTIPLE: the staged list sits ABOVE the button, which stays live so more files can be
            added without clearing what is already staged. */}
        {multiple && stagedList.length > 0 && (
          <Box
            {...dropHandlers}
            sx={{
              border: '1px solid', borderColor: 'divider', borderRadius: 1,
              px: 1, py: 0.5, mb: 0.5,
            }}
          >
            {stagedMultiList}
          </Box>
        )}
        <Box {...dropHandlers}>
          <Button
            variant="outlined"
            fullWidth
            onClick={openPicker}
            disabled={disabled}
            startIcon={(multiple ? false : !!file) ? <AttachFileIcon /> : <CloudUploadIcon />}
            sx={{
              justifyContent: 'flex-start',
              borderStyle: 'dashed',
              borderColor,
              textTransform: 'none',
              py: dense ? 1 : 1.5,
              bgcolor: isDragging ? 'action.hover' : 'transparent',
            }}
          >
            {(!multiple && file) ? stagedInline : (
              <Typography variant="body2" color="text.secondary">
                {promptText || (multiple
                  ? (stagedList.length > 0
                      ? 'Click to attach another document, or drag and drop'
                      : 'Click to attach documents, or drag and drop')
                  : 'Click to attach a document, or drag and drop')}
              </Typography>
            )}
            {hiddenInput}
          </Button>
        </Box>
        {(helperText || error) && (
          <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ display: 'block', mt: 0.5 }}>
            {error || helperText}
          </Typography>
        )}
      </Box>
    );
  }

  // ---- 'box' variant: dashed drop area (default) ----
  return (
    <Box>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
      )}
      <Box
        {...dropHandlers}
        onClick={openPicker}
        sx={{
          border: '2px dashed',
          borderColor,
          borderRadius: 1,
          p: dense ? 1.5 : 3,
          textAlign: 'center',
          cursor: disabled ? 'default' : 'pointer',
          bgcolor: isDragging ? 'action.hover' : 'transparent',
          opacity: disabled ? 0.6 : 1,
          transition: 'border-color 120ms, background-color 120ms',
          '&:hover': disabled ? {} : { borderColor: 'primary.main' },
        }}
      >
        {hiddenInput}
        {multiple ? (
          <>
            {stagedList.length > 0 && (
              <Box sx={{ mb: 1, textAlign: 'left' }}>{stagedMultiList}</Box>
            )}
            <CloudUploadIcon color="action" sx={{ mb: dense ? 0 : 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {promptText || (stagedList.length > 0
                ? 'Click to add another file, or drag and drop'
                : 'Click to select files, or drag and drop')}
            </Typography>
            {helperText && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {helperText}
              </Typography>
            )}
          </>
        ) : file ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
            <AttachFileIcon fontSize="small" color="action" />
            <Typography variant="body2" fontWeight="medium" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {file.name}
            </Typography>
            {file.size != null && (
              <Typography variant="caption" color="text.secondary">({formatFileSize(file.size)})</Typography>
            )}
            {!disabled && (
              <IconButton size="small" onClick={handleClear} aria-label="Remove file">
                <CloseIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ) : (
          <>
            <CloudUploadIcon color="action" sx={{ mb: dense ? 0 : 0.5 }} />
            <Typography variant="body2" color="text.secondary">
              {promptText || 'Click to select a file, or drag and drop'}
            </Typography>
            {helperText && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {helperText}
              </Typography>
            )}
          </>
        )}
      </Box>
      {error && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default FileDropZone;
